// AssoVitrineConfig.jsx — Phase G : Configuration de la vitrine publique (staff)
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import ImageUploader from '../components/ImageUploader';
import GalleryEditor from '../components/GalleryEditor';
import {
  Loader2, Save, Eye, EyeOff, Globe, Facebook, Instagram, Linkedin, Twitter,
  Image as ImageIcon, ExternalLink, Check, Copy, ChevronRight, Info, Sparkles, Palette,
} from 'lucide-react';

const DEFAULT = {
  publiee: false,
  accroche: '',
  sous_titre: '',
  description_longue: '',
  photo_couverture: '',
  photo_secondaire: '',
  galerie: [],
  liens_sociaux: { facebook: '', instagram: '', linkedin: '', twitter: '', site_web: '' },
  cta_principal: 'Découvrir nos événements',
  cta_secondaire: 'Rejoindre',
  sections: { hero: true, evenements: true, formules: true, sponsors: true, contact: true },
  contact: { email: '', telephone: '', adresse: '' },
};

export default function AssoVitrineConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customDomain, setCustomDomain] = useState('');
  const [vitrine, setVitrine] = useState(DEFAULT);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/assos/vitrine');
      setConfig(data);
      setVitrine({ ...DEFAULT, ...data.vitrine, sections: { ...DEFAULT.sections, ...(data.vitrine?.sections || {}) }, contact: { ...DEFAULT.contact, ...(data.vitrine?.contact || {}) }, liens_sociaux: { ...DEFAULT.liens_sociaux, ...(data.vitrine?.liens_sociaux || {}) } });
      setCustomDomain(data.custom_domain || '');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur de chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/assos/vitrine', { vitrine, custom_domain: customDomain });
      setConfig(data);
      toast.success('Vitrine enregistrée');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur');
    } finally { setSaving(false); }
  };

  if (loading || !config) {
    return <div className="page-container"><div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div></div>;
  }

  const previewUrl = `/asso/${config.slug}`;
  const fullPreviewUrl = `${window.location.origin}${previewUrl}`;

  return (
    <div className="page-container" data-testid="vitrine-config-page">
      <div className="page-header">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="label-uppercase mb-2 flex items-center gap-1.5" style={{ color: 'var(--theme-primary)' }}><Sparkles className="w-3 h-3" /> Vitrine publique</div>
            <h1 className="page-title">Page de vente de l&apos;association</h1>
            <p className="page-subtitle">Configurez votre page d&apos;accueil publique. Visiteurs et futurs membres y trouvent vos événements et formules.</p>
          </div>
          <div className="flex gap-2">
            <a href={previewUrl} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-1.5" data-testid="vitrine-preview-link"><Eye className="w-4 h-4" /> Aperçu</a>
            <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1.5" data-testid="vitrine-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer
            </button>
          </div>
        </div>
      </div>

      {/* Statut publication */}
      <div className="card !p-4 mb-5 flex items-center justify-between gap-3 flex-wrap" style={{ borderLeftWidth: 4, borderLeftColor: vitrine.publiee ? '#22D3A0' : '#94A3B8' }}>
        <div className="flex items-center gap-3 min-w-0">
          {vitrine.publiee ? <Eye className="w-5 h-5 text-emerald-600" /> : <EyeOff className="w-5 h-5 text-slate-400" />}
          <div>
            <div className="font-bold text-slate-900 text-sm">{vitrine.publiee ? 'Vitrine en ligne' : 'Vitrine en brouillon'}</div>
            <div className="text-xs text-slate-500 break-all">{vitrine.publiee ? <a href={previewUrl} target="_blank" rel="noreferrer" className="underline">{fullPreviewUrl}</a> : 'Activez la publication pour la rendre visible.'}</div>
          </div>
        </div>
        <Toggle checked={vitrine.publiee} onChange={(v) => setVitrine({ ...vitrine, publiee: v })} testid="vitrine-publish-toggle" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* COLONNE GAUCHE — Édition */}
        <div className="space-y-5">
          {/* Hero */}
          <Card title="Bannière d'accueil" icon={Sparkles}>
            <Field label="Accroche principale">
              <input type="text" value={vitrine.accroche} onChange={(e) => setVitrine({ ...vitrine, accroche: e.target.value })} maxLength={200} placeholder={`Bienvenue chez ${config.nom}`} className="input-base" data-testid="vitrine-accroche" />
            </Field>
            <Field label="Sous-titre">
              <input type="text" value={vitrine.sous_titre} onChange={(e) => setVitrine({ ...vitrine, sous_titre: e.target.value })} maxLength={200} placeholder="En une phrase, votre identité" className="input-base" data-testid="vitrine-soustitre" />
            </Field>
            <ImageUploader value={vitrine.photo_couverture} onChange={(url) => setVitrine({ ...vitrine, photo_couverture: url })} label="Photo de couverture" testid="vitrine-photo-couv" aspect="16/9" hint="Bannière du hero — PNG/JPG/WEBP, 5 Mo max" />
            <ImageUploader value={vitrine.photo_secondaire} onChange={(url) => setVitrine({ ...vitrine, photo_secondaire: url })} label="Photo secondaire (optionnel)" testid="vitrine-photo-sec" aspect="16/9" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="CTA principal">
                <input type="text" value={vitrine.cta_principal} onChange={(e) => setVitrine({ ...vitrine, cta_principal: e.target.value })} maxLength={60} className="input-base" data-testid="vitrine-cta-1" />
              </Field>
              <Field label="CTA secondaire">
                <input type="text" value={vitrine.cta_secondaire} onChange={(e) => setVitrine({ ...vitrine, cta_secondaire: e.target.value })} maxLength={60} className="input-base" data-testid="vitrine-cta-2" />
              </Field>
            </div>
          </Card>

          {/* Description */}
          <Card title="Description de l'association">
            <Field label="Texte principal" hint="3000 caractères max. Apparaît juste sous la bannière.">
              <textarea rows="6" value={vitrine.description_longue} onChange={(e) => setVitrine({ ...vitrine, description_longue: e.target.value })} maxLength={3000} className="input-base" placeholder="Racontez votre histoire, vos missions, vos valeurs…" data-testid="vitrine-desc-longue" />
              <div className="text-[10px] text-slate-400 mt-1 text-right">{vitrine.description_longue.length} / 3000</div>
            </Field>
          </Card>

          {/* Galerie photos */}
          <Card title="Galerie photos" icon={ImageIcon}>
            <p className="text-xs text-slate-500 mb-3">Mettez en valeur vos événements passés : ajoutez plusieurs photos (avec légende), affichées sur votre vitrine publique.</p>
            <GalleryEditor items={vitrine.galerie} onChange={(galerie) => setVitrine({ ...vitrine, galerie })} />
          </Card>

          {/* Sections visibles */}
          <Card title="Sections à afficher" icon={Eye}>
            <div className="space-y-2">
              {[
                { key: 'hero', label: 'Bannière d\'accueil' },
                { key: 'evenements', label: 'Liste des événements à venir' },
                { key: 'formules', label: 'Formules d\'adhésion' },
                { key: 'sponsors', label: 'Sponsors et partenaires' },
                { key: 'contact', label: 'Section contact' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100" data-testid={`vitrine-section-${key}`}>
                  <span className="text-sm font-medium text-slate-900">{label}</span>
                  <Toggle checked={vitrine.sections[key]} onChange={(v) => setVitrine({ ...vitrine, sections: { ...vitrine.sections, [key]: v } })} testid={`vitrine-section-toggle-${key}`} />
                </label>
              ))}
            </div>
          </Card>

          {/* Réseaux sociaux */}
          <Card title="Réseaux sociaux & liens">
            {[
              { key: 'facebook', icon: Facebook, placeholder: 'https://facebook.com/...' },
              { key: 'instagram', icon: Instagram, placeholder: '@votre_compte ou URL' },
              { key: 'linkedin', icon: Linkedin, placeholder: 'https://linkedin.com/...' },
              { key: 'twitter', icon: Twitter, placeholder: 'https://twitter.com/...' },
              { key: 'site_web', icon: Globe, placeholder: 'https://votre-site.com' },
            ].map(({ key, icon: Icon, placeholder }) => (
              <Field key={key} label={key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                  <input type="text" value={vitrine.liens_sociaux[key] || ''} onChange={(e) => setVitrine({ ...vitrine, liens_sociaux: { ...vitrine.liens_sociaux, [key]: e.target.value } })} placeholder={placeholder} className="input-base flex-1" data-testid={`vitrine-social-${key}`} />
                </div>
              </Field>
            ))}
          </Card>

          {/* Contact */}
          <Card title="Coordonnées de contact">
            <Field label="Email"><input type="email" value={vitrine.contact.email} onChange={(e) => setVitrine({ ...vitrine, contact: { ...vitrine.contact, email: e.target.value } })} className="input-base" data-testid="vitrine-contact-email" /></Field>
            <Field label="Téléphone"><input type="tel" value={vitrine.contact.telephone} onChange={(e) => setVitrine({ ...vitrine, contact: { ...vitrine.contact, telephone: e.target.value } })} className="input-base" data-testid="vitrine-contact-tel" /></Field>
            <Field label="Adresse"><input type="text" value={vitrine.contact.adresse} onChange={(e) => setVitrine({ ...vitrine, contact: { ...vitrine.contact, adresse: e.target.value } })} className="input-base" data-testid="vitrine-contact-adresse" /></Field>
          </Card>

          {/* Nom de domaine personnalisé */}
          <Card title="Nom de domaine personnalisé" icon={Globe}>
            <Field label="Votre domaine" hint="Ex : billetterie.refigeac.fr ou www.votre-asso.fr">
              <input type="text" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="ex: billetterie.votre-asso.fr" className="input-base" data-testid="vitrine-custom-domain" />
            </Field>
            {customDomain && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900" data-testid="vitrine-dns-instructions">
                <div className="font-bold mb-1.5 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Configuration DNS requise</div>
                <p className="mb-2">Pour activer ce domaine, ajoutez l&apos;enregistrement <strong>CNAME</strong> suivant chez votre registrar :</p>
                <pre className="bg-white border border-amber-100 rounded p-2 font-mono text-[11px] text-slate-700 overflow-x-auto select-all flex items-center gap-2">
                  <strong>{customDomain}</strong> &nbsp;CNAME&nbsp; <span className="text-slate-500">{window.location.host}</span>
                  <button onClick={() => { navigator.clipboard.writeText(`${customDomain} CNAME ${window.location.host}`); toast.success('Copié !'); }} className="ml-auto text-amber-700 hover:text-amber-900" aria-label="Copier"><Copy className="w-3.5 h-3.5" /></button>
                </pre>
                <p className="mt-2 text-[11px] opacity-80">La propagation DNS prend généralement entre 5 min et 24 h. Une fois propagée, votre vitrine sera accessible directement via <strong>https://{customDomain}</strong>.</p>
              </div>
            )}
          </Card>
        </div>

        {/* COLONNE DROITE — Aperçu live */}
        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start" data-testid="vitrine-preview-pane">
          <div className="card !p-0 overflow-hidden">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="flex gap-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                </div>
                <span className="font-mono">{fullPreviewUrl}</span>
              </div>
              <a href={previewUrl} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Ouvrir</a>
            </div>
            <div className="aspect-[4/3] bg-slate-100" data-testid="vitrine-preview-frame">
              <iframe src={previewUrl} className="w-full h-full border-0" title="Aperçu vitrine" key={vitrine.publiee + customDomain} />
            </div>
          </div>
          <div className="text-xs text-slate-500 px-1">
            L&apos;aperçu se met à jour après chaque <strong>enregistrement</strong>. Cliquez sur <strong>Aperçu</strong> pour voir le rendu complet en plein écran.
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="card !p-5">
      <h3 className="font-display font-bold text-slate-900 flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />} {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="label-uppercase block mb-1.5">{label}</label>
      {children}
      {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full transition-colors ${checked ? '' : 'bg-slate-300'}`} style={checked ? { background: 'var(--theme-primary)' } : {}} data-testid={testid}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  );
}
